import { Children, Fragment, isValidElement, useId } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { cn } from '../../lib/utils';

const EMPTY_VALUE = '__app_select_empty__';

function collectOptions(children) {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) return [];
    if (child.type === Fragment) return collectOptions(child.props.children);
    if (child.type === 'optgroup') {
      return collectOptions(child.props.children).map((option) => ({
        ...option,
        group: child.props.label,
      }));
    }
    if (child.type !== 'option') return [];
    return [{
      value: String(child.props.value ?? child.props.children ?? ''),
      label: child.props.children,
      disabled: Boolean(child.props.disabled),
    }];
  });
}

// Option children keep the same data-driven API as the existing forms.
export function AppSelect({
  children,
  value,
  defaultValue,
  onChange,
  label,
  className,
  id: suppliedId,
  disabled,
  required,
  name,
  ...triggerProps
}) {
  const generatedId = useId();
  const id = suppliedId || generatedId;
  const options = collectOptions(children);
  const empty = options.find((option) => option.value === '');
  const selectedValue = String(value ?? defaultValue ?? '');
  const radixValue = selectedValue === '' ? EMPTY_VALUE : selectedValue;
  const handleValueChange = (nextValue) => {
    const next = nextValue === EMPTY_VALUE ? '' : nextValue;
    onChange?.({ target: { value: next, name, id }, currentTarget: { value: next, name, id } });
  };

  return <div className="min-w-0 space-y-1 text-sm">
    {label && <label htmlFor={id}>{label}</label>}
    <Select value={radixValue} onValueChange={handleValueChange} disabled={disabled} required={required} name={name}>
      <SelectTrigger
        {...triggerProps}
        id={id}
        className={cn('min-h-11 rounded-xl border-[#E5E5EA] bg-white px-4 shadow-sm', className)}
      >
        <SelectValue placeholder={empty?.label || 'Bitte auswählen'} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option, index) => (
          <SelectItem
            key={`${option.value || EMPTY_VALUE}-${index}`}
            value={option.value || EMPTY_VALUE}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>;
}
